import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, MapPin, ArrowRight } from 'lucide-react';
import tournamentService, { Tournament } from '../../services/tournamentService';
import subtournamentService from '../../services/subtournamentService';
import ImageSlider from '../ImageSlider';
import { t as tr } from '../../i18n';

// ============================================
// "Neue Turniere" – horizontale Scroll-Sektion auf der Startseite.
// Zeigt die zuletzt bei DartCloud angelegten (kommenden) Community-Turniere,
// neueste zuerst. Gleiche Sektion existiert in der Flutter-App
// (home_new_tournaments.dart) zwischen PDC-Kalender und Darts-Wissen.
// ============================================

const ACCENT = '#3fced1';
const LIMIT = 10;

const timeAgo = (createdAtMs?: number): string => {
  if (!createdAtMs) return tr('web.component.home-new-tournaments.neu');
  const days = Math.floor((Date.now() - createdAtMs) / (1000 * 60 * 60 * 24));
  if (days <= 0) return tr('web.component.home-live-pdc-lead.heute');
  if (days === 1) return tr('web.component.home-new-tournaments.gestern');
  return tr('web.component.home-new-tournaments.vor-tagen', { days });
};

const firstDate = (t: Tournament): string => {
  if (!Array.isArray(t.dates) || t.dates.length === 0) return '';
  const ts = [...t.dates].sort((a, b) => a - b)[0];
  return new Date(ts).toLocaleDateString('de-DE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const locationLabel = (t: Tournament): string => {
  const city = t.address?.city?.trim();
  if (city) return city;
  const label = t.address?.label?.trim();
  if (label) return label;
  return tr('web.component.home-new-tournaments.online');
};

const HomeNewTournaments: React.FC = () => {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [compImages, setCompImages] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [res, subs] = await Promise.all([
          tournamentService.getTournaments({ limit: 500 }),
          subtournamentService.getAllSubtournaments().catch(() => []),
        ]);
        const all: Tournament[] = (res as any)?.data ?? [];
        // Turniere mit Competitions erben deren Bilder (Turnier selbst hat oft keins)
        const imagesByMain: Record<string, string[]> = {};
        for (const sub of subs) {
          const main = sub.mainTournament;
          if (!main) continue;
          const urls = [sub.image, ...(Array.isArray(sub.images) ? sub.images : [])]
            .map((img) => tournamentService.buildThumbnailUrl(img ?? null, 400))
            .filter((u): u is string => !!u);
          if (urls.length === 0) continue;
          imagesByMain[main] = [...(imagesByMain[main] ?? []), ...urls];
        }
        if (!cancelled) setCompImages(imagesByMain);
        const startOfToday = new Date().setHours(0, 0, 0, 0);

        const newest = all
          .filter((t) => {
            if (!t || t.visible === false || t.cancelled || !t.slug) return false;
            // Nur kommende/heutige Turniere (letzter Termin nicht in der Vergangenheit)
            if (!Array.isArray(t.dates) || t.dates.length === 0) return false;
            return Math.max(...t.dates) >= startOfToday;
          })
          .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))
          .slice(0, LIMIT);

        if (!cancelled) setTournaments(newest);
      } catch (err) {
        console.error('Neue Turniere konnten nicht geladen werden:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const scrollBy = (delta: number) => {
    scrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  };

  if (!loading && tournaments.length === 0) return null;

  return (
    <section aria-label={tr('web.component.home-new-tournaments.neue-turniere')}>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="section-heading text-base font-bold text-white">{tr('web.component.home-new-tournaments.neue-turniere')}</h2>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => navigate('/suchen')}
            className="mr-1 hidden items-center gap-1 text-xs font-semibold text-gray-400 transition-colors hover:text-[#3fced1] sm:flex"
          >
            {tr('web.component.events-filter-bar.alle')}{' '}<ArrowRight size={13} />
          </button>
          {!loading && tournaments.length > 3 && (
            <>
              <button
                type="button"
                aria-label={tr('web.component.chats-manager.zurueck')}
                onClick={() => scrollBy(-360)}
                className="hidden h-7 w-7 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900 text-gray-400 transition-colors hover:border-[#3fced1]/50 hover:text-white sm:flex"
              >
                <ChevronLeft size={15} />
              </button>
              <button
                type="button"
                aria-label={tr('web.component.home-darts-knowledge.weiter')}
                onClick={() => scrollBy(360)}
                className="hidden h-7 w-7 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900 text-gray-400 transition-colors hover:border-[#3fced1]/50 hover:text-white sm:flex"
              >
                <ChevronRight size={15} />
              </button>
            </>
          )}
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto scroll-smooth pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {loading
          ? Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-[240px] w-[240px] shrink-0 animate-pulse rounded-2xl border border-zinc-800 bg-zinc-900"
              />
            ))
          : tournaments.map((t) => (
              <TournamentCard
                key={t.id ?? t._id}
                tournament={t}
                compImages={compImages[(t.id ?? t._id) as string] ?? []}
                onClick={() => navigate(`/turnier/${t.slug}`)}
              />
            ))}
      </div>
    </section>
  );
};

const TournamentCard: React.FC<{ tournament: Tournament; compImages: string[]; onClick: () => void }> = ({
  tournament,
  compImages,
  onClick,
}) => {
  const [imgError, setImgError] = useState(false);
  // Leerstring/Whitespace defensiv als "kein Bild" behandeln (`?? ` greift bei "" nicht)
  const pickFile = (v?: string | null): string | null => (v && v.trim() ? v : null);
  const file = pickFile(tournament.image) ?? pickFile(tournament.images?.[0]);
  const imageUrl = tournamentService.buildThumbnailUrl(file, 400);
  const showImage = imageUrl && !imgError;
  // Kein eigenes Bild? Dann die Competition-Bilder als Slider zeigen
  const showCompSlider = !showImage && compImages.length > 0;

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-[240px] shrink-0 flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 text-left transition-colors hover:border-[#3fced1]/50"
    >
      <div className="relative aspect-video w-full shrink-0 overflow-hidden bg-zinc-800">
        {showImage ? (
          <img
            src={imageUrl as string}
            alt=""
            loading="lazy"
            onError={() => setImgError(true)}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
        ) : showCompSlider ? (
          <ImageSlider images={compImages} alt={tournament.name ?? ''} />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-900">
            <img
              src="/icon.png"
              alt=""
              loading="lazy"
              className="h-10 w-10 rounded-lg shadow-[0_0_24px_rgba(63,206,210,0.25)]"
            />
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col p-2.5">
        {/* Chip zeigt das TURNIERDATUM (2026-08-24) — das Erstellungsdatum
            ("Vor 2 Tagen") hat nur verwirrt */}
        <span
          className="mb-2 self-start rounded px-2 py-0.5 text-[10px] font-bold text-black"
          style={{ backgroundColor: ACCENT }}
        >
          {firstDate(tournament) || tr('web.component.home-new-tournaments.termin-folgt')}
        </span>
        <h3 className="line-clamp-2 text-[13px] font-semibold leading-snug text-white transition-colors group-hover:text-[#3fced1]">
          {tournament.name ?? tr('web.component.create-entry-modal.turnier')}
        </h3>
        <div className="mt-auto flex items-center gap-1 pt-1.5 text-[11px] text-gray-500">
          <MapPin size={11} className="shrink-0" />
          <span className="truncate">{locationLabel(tournament)}</span>
        </div>
      </div>
    </button>
  );
};

export default HomeNewTournaments;
